import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Zap, ChevronRight, Check, SkipForward, BellRing, AlertTriangle, PlayCircle, Timer } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/store/AppContext";
import { useAuth } from "@/store/AuthContext";
import {
  useHabitMap,
  useMemberMap,
  useCategoriesMap,
  useGroup,
  useGroupMembers,
  useMyPendingNudges,
  useGroupOverlap,
  useMarkHabit,
  useMyInbox,
  useMarkNudgeSeen,
} from "@/lib/hooks";
import { useHabitSession, formatMs } from "@/lib/useHabitSession";
import CountdownBadge from "@/components/CountdownBadge";
import AdSlot from "@/components/AdSlot";

function toMinutes(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function sortedToday(scheduled) {
  return [...scheduled].sort((a, b) => toMinutes(a.start) - toMinutes(b.start));
}

function findNext(scheduled) {
  if (!scheduled.length) return null;
  const now = new Date();
  const nowM = now.getHours() * 60 + now.getMinutes();
  const upcoming = sortedToday(scheduled).find((s) => toMinutes(s.start) >= nowM);
  return upcoming || sortedToday(scheduled)[0];
}

export default function Dashboard() {
  const { scheduled, sendNudge, authed } = useApp();
  const { user, login } = useAuth();
  const habitMap = useHabitMap();
  const memberMap = useMemberMap();
  const catMap = useCategoriesMap();
  const { data: group } = useGroup();
  const { data: members = [] } = useGroupMembers();
  const { data: pendingNudges } = useMyPendingNudges();
  const { data: overlapDoc } = useGroupOverlap();
  const { data: inbox = [] } = useMyInbox();
  const markHabit = useMarkHabit();
  const markSeen = useMarkNudgeSeen();
  const overlap = overlapDoc?.grid || [];

  // "Skip today" advances the display through the sorted list of today's habits
  // without changing the underlying schedule. Local-only until the next reload.
  const [skipIndex, setSkipIndex] = useState(0);
  const todaySorted = useMemo(() => sortedToday(scheduled), [scheduled]);
  const startIdx = useMemo(() => {
    if (!todaySorted.length) return 0;
    const now = new Date();
    const nowM = now.getHours() * 60 + now.getMinutes();
    const idx = todaySorted.findIndex((s) => toMinutes(s.start) >= nowM);
    return idx >= 0 ? idx : 0;
  }, [todaySorted]);
  const currentIdx = todaySorted.length
    ? (startIdx + skipIndex) % todaySorted.length
    : 0;
  const next = todaySorted[currentIdx] || null;
  const nextHabit = next ? habitMap[next.template_id] : null;

  // Habit session (begin -> running -> complete-enabled)
  const session = useHabitSession({
    userId: user?.user_id,
    templateId: next?.template_id,
    durationMin: nextHabit?.durationMin,
  });

  const onBegin = () => {
    if (!next) return;
    session.begin();
    toast("You've begun.", {
      description: `${nextHabit?.title} · ${nextHabit?.durationMin} min. Screen will stay awake.`,
    });
  };

  const onComplete = async () => {
    if (!next) return;
    try {
      await markHabit.mutateAsync({ template_id: next.template_id, status: "completed" });
      session.clear();
      toast("Marked complete.", { description: `${nextHabit?.title} · streak reset` });
      // After completing, advance to the next habit if any
      if (todaySorted.length > 1) setSkipIndex((v) => v + 1);
    } catch (e) {
      toast.error("Couldn't save", { description: e?.response?.data?.detail || e.message });
    }
  };

  const onSkip = async () => {
    if (!next) return;
    try {
      const res = await markHabit.mutateAsync({ template_id: next.template_id, status: "skipped" });
      if (res.auto_nudge_forced && res.consecutive_misses === 3) {
        toast("Auto-nudge just turned on.", {
          description: "Your circle will be pinged the next time this habit starts.",
        });
      } else {
        toast("Skipped for today.", {
          description: `${res.consecutive_misses}/3 misses · showing the next habit.`,
        });
      }
      setSkipIndex((v) => v + 1);
    } catch (e) {
      toast.error("Couldn't save", { description: e?.response?.data?.detail || e.message });
    }
  };

  const unseenAutoNudges = (inbox || []).filter((n) => n.is_auto && !n.seen);

  // For anonymous users: fall back to first 3 group members with a mock habit label
  const displayNudges =
    pendingNudges && pendingNudges.length
      ? pendingNudges
      : members.slice(0, 3).map((m) => ({
          member: m,
          habit: habitMap["h_1"],
          when_label: "in 12 min",
        }));

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <section className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="next-habit-hero">
        <div className="md:col-span-8 card-editorial p-8 md:p-10 flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <span className="eyebrow">Next in your rhythm</span>
            <span className="tag-pill" data-testid="hero-group-name">{group?.name || "Loading circle…"}</span>
          </div>
          {nextHabit ? (
            <>
              <div>
                <p className="eyebrow mb-2">{catMap[nextHabit.category] || nextHabit.category} · {nextHabit.durationMin} min</p>
                <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-stone-900 leading-none">
                  {nextHabit.title}
                </h1>
                <p className="text-stone-600 mt-3 max-w-lg leading-relaxed">{nextHabit.description}</p>
              </div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-t border-stone-100 pt-6">
                <div className="flex items-center gap-6" data-testid="hero-timer-block">
                  <div>
                    <p className="eyebrow mb-2">
                      {session.isRunning ? "Time remaining" : session.isReadyToComplete ? "Ready to close" : "Starts in"}
                    </p>
                    {session.startedAt ? (
                      <p
                        className="font-serif text-5xl leading-none tracking-tight text-stone-900 data-mono"
                        data-testid="hero-live-timer"
                      >
                        {formatMs(session.remainingMs)}
                      </p>
                    ) : (
                      <CountdownBadge start={next.start} size="lg" />
                    )}
                    <p className="data-mono mt-1 text-stone-500">
                      {session.isRunning
                        ? `until ${nextHabit.durationMin}m are done`
                        : session.isReadyToComplete
                        ? "the interval has elapsed"
                        : `at ${next.start}, local`}
                    </p>
                  </div>
                  {/* Primary action anchored to the timer */}
                  {!session.startedAt ? (
                    <button
                      className="btn-accent"
                      onClick={onBegin}
                      data-testid="hero-begin-btn"
                    >
                      <Zap size={16} /> Begin together
                    </button>
                  ) : (
                    <button
                      className="btn-accent"
                      onClick={onComplete}
                      disabled={!session.isReadyToComplete || markHabit.isPending}
                      data-testid="hero-complete-btn"
                      title={
                        session.isReadyToComplete
                          ? "Close this session"
                          : `Enabled once the ${nextHabit.durationMin} min interval is over`
                      }
                    >
                      <Check size={16} /> Complete
                    </button>
                  )}
                </div>

                {/* Secondary line: Skip today + a small preview of the next-up habit */}
                <div className="flex flex-col items-start md:items-end gap-2">
                  {!session.startedAt ? (
                    <>
                      <button
                        onClick={onSkip}
                        disabled={markHabit.isPending}
                        className="btn-outline"
                        data-testid="hero-skip-btn"
                        title="Skip today — auto-nudge turns on at 3 consecutive skips"
                      >
                        <SkipForward size={16} /> Skip today
                      </button>
                      {todaySorted.length > 1 && (() => {
                        const upcomingIdx = (currentIdx + 1) % todaySorted.length;
                        const upcoming = todaySorted[upcomingIdx];
                        const upcomingHabit = habitMap[upcoming.template_id];
                        return upcomingHabit ? (
                          <p className="eyebrow text-stone-500" data-testid="hero-skip-preview">
                            next up · {upcomingHabit.title} at {upcoming.start}
                          </p>
                        ) : null;
                      })()}
                    </>
                  ) : (
                    <div
                      className="flex items-center gap-2 text-xs text-stone-500"
                      data-testid="hero-running-badge"
                    >
                      <span className="w-2 h-2 rounded-full bg-terracotta animate-pulse" />
                      Session in progress · screen held awake
                    </div>
                  )}
                </div>
              </div>

              {/* Live progress bar visible while running */}
              {session.startedAt && (
                <div
                  className="h-1.5 w-full rounded-full bg-stone-100 overflow-hidden"
                  data-testid="hero-progress"
                  role="progressbar"
                  aria-valuenow={Math.round(session.progressPct)}
                >
                  <div
                    className="h-full bg-terracotta transition-[width] duration-500 ease-linear"
                    style={{ width: `${session.progressPct}%` }}
                  />
                </div>
              )}

              {/* De-emphasized secondary link */}
              <div className="pt-2 flex flex-wrap items-center gap-3">
                <Link
                  to="/calendar"
                  className="text-xs text-stone-500 hover:text-stone-800 hover:underline inline-flex items-center gap-1"
                  data-testid="hero-view-calendar"
                >
                  See circle calendar <ChevronRight size={12} />
                </Link>
                {authed && (
                  <span className="text-xs text-stone-400" data-testid="hero-skip-count-hint">
                    · skipping cycles through today's list, doesn't remove anything
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="py-10 text-center">
              <p className="font-serif text-2xl text-stone-800">Nothing scheduled yet.</p>
              <Link to="/library" className="btn-accent mt-4 inline-flex" data-testid="hero-empty-cta">
                Pick your first habit
              </Link>
            </div>
          )}
        </div>

        <aside className="md:col-span-4 card-editorial p-6 flex flex-col gap-5" data-testid="circle-snapshot">
          <div>
            <span className="eyebrow">Your circle</span>
            <h3 className="font-serif text-3xl text-stone-900 mt-1 leading-tight">{group?.name || "…"}</h3>
            <p className="text-sm text-stone-600 mt-1">{group?.tagline}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {members.slice(0, 4).map((m) => (
                <img key={m.id} src={m.avatar} alt={m.name} className="w-9 h-9 rounded-full object-cover border-2 border-white" />
              ))}
            </div>
            <span className="data-mono">{(group?.memberCount ?? members.length) + (authed ? 0 : 0)} members · {group?.matchScore ?? "—"}% match</span>
          </div>
          <div className="border-t border-stone-100 pt-4">
            <p className="eyebrow mb-2">Peer nudges — awaiting you</p>
            {!authed && (
              <p className="text-xs text-stone-500 mb-2">
                <button onClick={login} className="underline text-terracotta">Sign in</button> to actually send them.
              </p>
            )}
            <ul className="space-y-3" data-testid="pending-nudges">
              {displayNudges.map((n) => (
                <li key={n.member.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img src={n.member.avatar} alt={n.member.name} className="w-8 h-8 rounded-full object-cover" />
                    <div>
                      <p className="text-sm text-stone-800 leading-tight">{n.member.name}</p>
                      <p className="text-xs text-stone-500">{n.habit?.title} · {n.when_label}</p>
                    </div>
                  </div>
                  <button
                    className="rounded-full p-2 hover:bg-stone-100"
                    onClick={() => sendNudge({ recipient_id: n.member.id, recipient_name: n.member.name, habit_id: n.habit?.id })}
                    data-testid={`nudge-btn-${n.member.id}`}
                    aria-label={`Nudge ${n.member.name}`}
                  >
                    <Bell size={16} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </section>

      {authed && unseenAutoNudges.length > 0 && (
        <section className="card-editorial p-6 md:p-7 border-l-4 border-terracotta" data-testid="auto-nudge-inbox">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <span className="eyebrow flex items-center gap-2"><BellRing size={12} /> Auto-nudges just in</span>
              <h2 className="font-serif text-2xl sm:text-3xl text-stone-900 mt-1">
                Your circle just started {unseenAutoNudges.length === 1 ? "a habit" : "some habits"}.
              </h2>
            </div>
            <span className="data-mono text-xs text-stone-500" data-testid="auto-nudge-count">
              {unseenAutoNudges.length} unread
            </span>
          </div>
          <ul className="space-y-2" data-testid="auto-nudge-list">
            {unseenAutoNudges.slice(0, 5).map((n) => (
              <li
                key={n.id}
                className="flex items-center justify-between gap-3 py-2 border-t border-stone-100"
                data-testid={`auto-nudge-item-${n.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center">
                    <BellRing size={15} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm text-stone-900 leading-tight truncate">
                      <span className="font-medium">{n.sender_name}</span>{" "}
                      just started <span className="italic">{n.habit?.title || "a habit"}</span>
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest bg-terracotta/10 text-terracotta">
                        auto-nudge
                      </span>
                      {n.sender_miss_count >= 3 && (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle size={11} /> dropped {n.sender_miss_count} times
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => markSeen.mutate(n.id)}
                  className="btn-outline text-xs !min-h-[2.25rem] !py-1"
                  data-testid={`auto-nudge-dismiss-${n.id}`}
                >
                  <Check size={13} /> Got it
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-5" data-testid="today-timeline">
        <div className="flex items-baseline justify-between">
          <div>
            <span className="eyebrow">Today · your quiet order</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 mt-1">The rhythm ahead</h2>
          </div>
          <Link to="/library" className="text-sm text-terracotta hover:underline" data-testid="add-more-link">
            Add another habit →
          </Link>
        </div>
        {scheduled.length === 0 ? (
          <p className="text-sm text-stone-500">Nothing on today's list yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sortedToday(scheduled).map(({ template_id, start }) => {
              const h = habitMap[template_id];
              if (!h) return null;
              return (
                <div key={template_id} className="card-editorial p-5 flex items-center justify-between" data-testid={`today-item-${template_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 text-center">
                      <p className="data-mono text-lg text-stone-900">{start}</p>
                      <p className="eyebrow text-[10px] mt-1">{h.durationMin}m</p>
                    </div>
                    <div className="border-l border-stone-200 pl-4">
                      <p className="font-serif text-xl text-stone-900 leading-tight">{h.title}</p>
                      <p className="text-xs text-stone-500 mt-0.5">{catMap[h.category] || h.category}</p>
                    </div>
                  </div>
                  <CountdownBadge start={start} />
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-7 card-editorial p-6 md:p-8" data-testid="overlap-panel">
          <span className="eyebrow">This week · with your circle</span>
          <h3 className="font-serif text-3xl text-stone-900 mt-1">You&apos;ll cross paths <span className="italic text-terracotta">often</span>.</h3>
          <p className="text-sm text-stone-600 mt-2 max-w-lg">
            Members from five timezones sit down at the same hour, more often than you think. Below is the density of hands raised across the week.
          </p>
          <div className="mt-5 grid grid-cols-7 gap-1.5">
            {overlap.length ? (
              overlap.flat().slice(0, 7 * 12).map((v, i) => {
                const opacityMap = ["bg-stone-100", "bg-sage/25", "bg-sage/40", "bg-sage/55", "bg-sage/75", "bg-sage"];
                return <div key={i} className={`h-5 rounded ${opacityMap[v] || opacityMap[0]}`} />;
              })
            ) : (
              Array.from({ length: 7 * 12 }).map((_, i) => (
                <div key={i} className="h-5 rounded bg-stone-100" />
              ))
            )}
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="eyebrow">Mon → Sun</span>
            <div className="flex items-center gap-2 eyebrow">
              quieter
              <span className="w-4 h-3 rounded bg-stone-100" />
              <span className="w-4 h-3 rounded bg-sage/40" />
              <span className="w-4 h-3 rounded bg-sage/75" />
              <span className="w-4 h-3 rounded bg-sage" />
              louder
            </div>
          </div>
        </div>
        <div className="md:col-span-5">
          <AdSlot variant="inline" data-testid="dashboard-inline-ad" />
        </div>
      </section>
    </div>
  );
}
